import type { ComponentProps } from 'react';
import type { Control, FieldPath, FieldValues } from 'react-hook-form';

import { Select } from '@/components/common/select';

import { FormField } from '../form-field';

type SelectBaseProps = Omit<ComponentProps<typeof Select>, 'value' | 'onChange' | 'mode'>;

export type SelectFieldProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
> = SelectBaseProps & {
  name: TName;
  control: Control<TFieldValues>;
  label?: string;
  help?: string;
  required?: boolean;
};

export function SelectField<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>({ name, control, label, help, required, ...rest }: SelectFieldProps<TFieldValues, TName>) {
  return (
    <FormField name={name} control={control} label={label} help={help} required={required}>
      {({ field, fieldState }) => (
        <Select
          {...rest}
          id={name}
          style={{ width: '100%', ...rest.style }}
          status={fieldState.error ? 'error' : undefined}
          value={field.value ?? undefined}
          // antd's clear control answers `undefined`, which RHF stores as "no value at all" and
          // leaves the rendered selection untouched (ADO 112241). `null` is a real value, so the
          // field re-renders empty; `toFilters` already folds it back to "no filter".
          onChange={(next, option) => field.onChange(next ?? null, option)}
          onBlur={field.onBlur}
          ref={field.ref}
        />
      )}
    </FormField>
  );
}
